import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDrive, Trash2 } from 'lucide-react';
import { PageHeader, Section } from '../components/form';
import { Alert, Badge, Button } from '../components/ui';
import { api } from '../lib/api';

interface StorageArea {
  id: string;
  label: string;
  description: string;
  path: string;
  bytes: number;
  files: number;
  cleanable: 'truncate' | 'delete' | null;
  cleanupWarning: string | null;
}

interface Overview {
  disk: { totalBytes: number; freeBytes: number; usedBytes: number; usedPercent: number };
  areas: StorageArea[];
}

const size = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

/**
 * What is using the disk.
 *
 * Written after 15 GB of log files took a 38 GB disk to 70% overnight and nothing in the product
 * would have shown it. On a PBX a full disk is not a degraded feature: Postgres stops writing WAL
 * and calls stop. So this is deliberately a page about one number — how close the box is to that
 * — and about the one directory that realistically gets it there.
 */
export function Storage() {
  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['storage'],
    queryFn: () => api.get<Overview>('/api/v1/system/storage'),
  });

  const clean = useMutation({
    mutationFn: (id: string) =>
      api.post<{ freedBytes: number; files: number }>(`/api/v1/system/storage/${id}/clean`, {
        confirm: 'clean',
      }),
    onSuccess: (result) => {
      setNote(
        result.freedBytes > 0
          ? `Freed ${size(result.freedBytes)} across ${result.files} file(s).`
          : 'Nothing to free — that area was already empty.',
      );
      void queryClient.invalidateQueries({ queryKey: ['storage'] });
    },
  });

  const disk = data?.disk;
  const critical = (disk?.usedPercent ?? 0) >= 90;
  const warning = (disk?.usedPercent ?? 0) >= 75;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Storage"
        description="What is using the disk, and what is safe to clear."
      />

      {note && <Alert>{note}</Alert>}
      {clean.error && <Alert>{(clean.error as Error).message}</Alert>}

      <Section title="Disk">
        {isLoading || !disk ? (
          <p className="text-sm text-muted-foreground">Measuring…</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="flex items-center gap-2 text-sm">
                <HardDrive className="size-4 text-muted-foreground" />
                <strong className="text-lg tabular-nums">{size(disk.freeBytes)}</strong>
                <span className="text-muted-foreground">free of {size(disk.totalBytes)}</span>
              </span>
              <Badge tone={critical ? 'destructive' : warning ? 'warning' : 'success'}>
                {disk.usedPercent}% used
              </Badge>
            </div>

            <div
              className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
              role="img"
              aria-label={`${disk.usedPercent}% of the disk is used`}
            >
              <div
                className={`h-full rounded-full transition-all ${
                  critical ? 'bg-destructive' : warning ? 'bg-amber-500' : 'bg-primary'
                }`}
                style={{ width: `${Math.min(100, disk.usedPercent)}%` }}
              />
            </div>

            {critical && (
              <Alert>
                <strong>The disk is nearly full.</strong> When it fills, Postgres stops writing and
                calls stop — this is an outage, not a degraded feature. Clear the largest area
                below, or move recordings off the box.
              </Alert>
            )}
          </div>
        )}
      </Section>

      <Section
        title="What is using it"
        description="Largest first. Only areas the PBX can safely empty offer a Clear button."
      >
        <div className="divide-y divide-border rounded-[--radius] border border-border">
          {(data?.areas ?? []).map((area) => (
            <div key={area.id} className="flex flex-wrap items-start gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{area.label}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{area.description}</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">{area.path}</p>
              </div>

              <div className="text-right">
                <p className="tabular-nums font-medium">{size(area.bytes)}</p>
                <p className="text-xs text-muted-foreground">
                  {area.files.toLocaleString()} file{area.files === 1 ? '' : 's'}
                </p>
              </div>

              <div className="w-28 text-right">
                {area.cleanable ? (
                  <Button
                    variant="secondary"
                    className="h-8 px-2 text-sm"
                    disabled={clean.isPending || area.bytes === 0}
                    onClick={() => {
                      if (confirm(`Clear ${area.label}?\n\n${area.cleanupWarning ?? ''}`)) {
                        clean.mutate(area.id);
                      }
                    }}
                  >
                    <Trash2 className="mr-1 size-3.5" />
                    Clear
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">Managed automatically</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/*
        The honest bit. Recordings are the only thing here that grows without bound in normal use,
        and they are governed by retention rather than by a button — because a Clear button next
        to somebody's call recordings is an accident waiting for a tired administrator.
      */}
      <Section title="What is not offered here">
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">
              Recordings and voicemail have no Clear button.
            </strong>{' '}
            They are somebody's records, and a one-click delete next to them is an accident waiting
            to happen. Recordings expire on the retention period set in Settings; voicemail is
            deleted per message.
          </li>
          <li>
            <strong className="text-foreground">Logs are rotated on the host</strong> at 100 MB,
            keeping five compressed copies. Clearing here is for when something has already filled
            the disk and you want the space back now.
          </li>
          <li>
            <strong className="text-foreground">
              Old generated configuration is kept on purpose
            </strong>{' '}
            — it is what a rollback restores after a bad apply. It is small.
          </li>
        </ul>
      </Section>
    </div>
  );
}
